// rtlsMap.js — RTLS facility map with Framer Motion expand animation + resize sync

import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import {
  motion,
  useMotionValue,
  useMotionValueEvent,
  animate as fmAnimate,
} from "framer-motion";
import RtlsFilter from "./rtlsFilter";
import { useHeatmap, HEAT_GRADIENT_CSS } from "../hooks/useHeatmap";
import {
  useSpaghettiMap,
  START_MARKER_COLOR_CSS,
  END_MARKER_COLOR_CSS,
} from "../hooks/useSpaghettiMap"; // ← SPAGHETTI
import {
  useWorkflowMap,
  HEAT_GRADIENT_CSS as WORKFLOW_HEAT_GRADIENT_CSS,
} from "../hooks/useWorkflowMap"; // ← WORKFLOW
import { usePlaybackMap, PLAYBACK_SPEEDS } from "../hooks/usePlaybackMap";
import "./rtlsMap.css";

// ─── Configuration ────────────────────────────────────────────────────────────
const RTLS_URL = (
  process.env.REACT_APP_RTLS_URL || "http://rtlsos.zmodular.com/riot"
)
  .replace(/[;'"]/g, "")
  .replace(/\/+$/, "");
const RTLS_USER = process.env.REACT_APP_RTLS_USER;
const RTLS_PASS = process.env.REACT_APP_RTLS_PASS;

const RTLS_LOCATION_ID = 8;

// ─── Close animation duration — must match rtlsf-drop-out in rtlsMap.css ─────
const FILTER_CLOSE_MS = 180;

// ─── Connection state ─────────────────────────────────────────────────────────
const CONN = {
  IDLE: "idle",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  RECONNECTING: "reconnecting",
  ERROR: "error",
};

const CONN_LABELS = {
  [CONN.IDLE]: "Initializing",
  [CONN.CONNECTING]: "Connecting",
  [CONN.CONNECTED]: "Live",
  [CONN.RECONNECTING]: "Reconnecting",
  [CONN.ERROR]: "Connection Error",
};

const CONN_COLORS = {
  [CONN.IDLE]: "#a0aec0",
  [CONN.CONNECTING]: "#d69e2e",
  [CONN.CONNECTED]: "#38a169",
  [CONN.RECONNECTING]: "#d69e2e",
  [CONN.ERROR]: "#cc0000",
};

// ─── Global script loader ─────────────────────────────────────────────────────
let globalScriptLoaded = false;
let globalScriptLoading = false;
let globalRiotInitialized = false;
const scriptCallbacks = [];

function loadKinexonScript(callback) {
  if (globalScriptLoaded && window.Riot) {
    callback(true);
    return;
  }
  if (globalScriptLoading) {
    scriptCallbacks.push(callback);
    return;
  }

  const existingScript = document.querySelector('script[src*="jsapi.js"]');
  if (existingScript) {
    globalScriptLoading = true;
    scriptCallbacks.push(callback);
    const checkInterval = setInterval(() => {
      if (window.Riot) {
        clearInterval(checkInterval);
        globalScriptLoaded = true;
        globalScriptLoading = false;
        scriptCallbacks.splice(0).forEach((cb) => cb(true));
      }
    }, 100);
    setTimeout(() => {
      if (globalScriptLoading) {
        clearInterval(checkInterval);
        globalScriptLoading = false;
        console.error("[RtlsMap] Timeout waiting for window.Riot");
        scriptCallbacks.splice(0).forEach((cb) => cb(false));
      }
    }, 10000);
    return;
  }

  globalScriptLoading = true;
  scriptCallbacks.push(callback);

  const scriptUrl = RTLS_URL + "/jsapi.js";
  const script = document.createElement("script");
  script.src = scriptUrl;
  script.async = true;
  script.type = "text/javascript";

  script.onload = () => {
    const checkRiot = () => {
      if (window.Riot) {
        globalScriptLoaded = true;
        globalScriptLoading = false;
        scriptCallbacks.splice(0).forEach((cb) => cb(true));
      } else {
        setTimeout(checkRiot, 100);
      }
    };
    setTimeout(checkRiot, 50);
  };

  script.onerror = (err) => {
    console.error("[RtlsMap] Script load failed:", scriptUrl, err);
    globalScriptLoading = false;
    globalScriptLoaded = false;
    scriptCallbacks.splice(0).forEach((cb) => cb(false));
  };

  document.head.appendChild(script);
}

// ─── Spring config ────────────────────────────────────────────────────────────
const EXPAND_SPRING = {
  type: "spring",
  damping: 32,
  stiffness: 280,
  mass: 0.8,
};

// ─── Facility bounds ──────────────────────────────────────────────────────────
const RTLS_BOUNDS = {
  xMin: -224.198,
  xMax: 68.133,
  yMin: 53.83,
  yMax: 101.175,
};

const MAP_HOME_BOUNDS_W = RTLS_BOUNDS.xMax - RTLS_BOUNDS.xMin;
const MAP_HOME_BOUNDS_H = RTLS_BOUNDS.yMax - RTLS_BOUNDS.yMin;
const MAP_HOME_PADDING = 0.88;
const MAP_HOME_ZOOM_FALLBACK = 0.1;

// ─── Component ────────────────────────────────────────────────────────────────
const RtlsMap = ({
  isHomePage = false,
  filterQuery = null,
  activeRtlsFn = null,
  rtlsSelectedObject = null,
  onRtlsFilterApply = null,
  onRtlsFnClose = null,
}) => {
  // ── UI state ────────────────────────────────────────────────────────────────
  const [isExpanded, setIsExpanded] = useState(false);
  const [connStatus, setConnStatus] = useState(CONN.IDLE);
  const [livePositions, setLivePositions] = useState([]);

  // ── Map-function popup state ─────────────────────────────────────────────────
  const [internalMapFn, setInternalMapFn] = useState(null);
  const effectiveMapFn = activeRtlsFn ?? internalMapFn;
  const effectiveMapFnRef = useRef(effectiveMapFn);
  useEffect(() => {
    effectiveMapFnRef.current = effectiveMapFn;
  }, [effectiveMapFn]);

  // Tracks the previous externally-driven activeRtlsFn value so we can tell
  // a parent-driven *switch* (heatmap → spaghetti) apart from a first
  // activation (null → heatmap) — see the sync effect below.
  const prevActiveRtlsFnRef = useRef(activeRtlsFn);
  // Tracks whatever was actually in effect right before (internal OR
  // external) so we know what to clear even if the thing being replaced
  // was opened via the in-map buttons rather than the activeRtlsFn prop.
  const prevEffectiveMapFnRef = useRef(effectiveMapFn);

  // ── Filter closing animation state ───────────────────────────────────────────
  const [isFilterClosing, setIsFilterClosing] = useState(false);
  const filterCloseTimerRef = useRef(null);

  const beginFilterClose = useCallback((afterClose) => {
    if (filterCloseTimerRef.current) return;
    setIsFilterClosing(true);
    filterCloseTimerRef.current = setTimeout(() => {
      filterCloseTimerRef.current = null;
      setIsFilterClosing(false);
      afterClose?.();
    }, FILTER_CLOSE_MS);
  }, []);

  const cancelFilterClose = useCallback(() => {
    if (filterCloseTimerRef.current) {
      clearTimeout(filterCloseTimerRef.current);
      filterCloseTimerRef.current = null;
    }
    setIsFilterClosing(false);
  }, []);

  useEffect(() => {
    return () => {
      if (filterCloseTimerRef.current)
        clearTimeout(filterCloseTimerRef.current);
    };
  }, []);

  const filterMode = activeRtlsFn ? "dateOnly" : "full";

  // ── ALL refs declared before any hook that consumes them ────────────────────
  const mapContainerRef = useRef(null);
  const riotMapRef = useRef(null);
  const riotRef = useRef(null);
  const apiRef = useRef(null);
  const posSubRef = useRef(null);
  const loggedInRef = useRef(false);
  const mountedRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const unsubscribersRef = useRef({
    connectionStatus: null,
    error: null,
    identityReset: null,
  });
  const dynamicResizeRef = useRef(null);
  const resizeRafRef = useRef(null);
  const spacerRef = useRef(null);
  const isExpandedRef = useRef(false);

  // ── Heatmap hook ─────────────────────────────────────────────────────────────
  const {
    isActive: heatmapActive,
    isLoading: heatmapLoading,
    error: heatmapError,
    cellCount: heatmapCellCount,
    minCount: heatmapMinCount,
    maxCount: heatmapMaxCount,
    apply: applyHeatmap,
    clear: clearHeatmap,
  } = useHeatmap(riotMapRef);

  // ── Spaghetti map hook ── ← SPAGHETTI ────────────────────────────────────────
  const {
    isActive: spaghettiActive,
    isLoading: spaghettiLoading,
    error: spaghettiError,
    trackCount: spaghettiTrackCount,
    pointCount: spaghettiPointCount,
    apply: applySpaghetti,
    clear: clearSpaghetti,
  } = useSpaghettiMap(riotMapRef);

  // ── Workflow map hook ── ← WORKFLOW ──────────────────────────────────────────
  const {
    isActive: workflowActive,
    isLoading: workflowLoading,
    error: workflowError,
    positionCount: workflowPositionCount,
    binCount: workflowBinCount,
    windowCount: workflowWindowCount,
    windows: workflowWindows,
    path: workflowPath,
    minCount: workflowMinCount,
    maxCount: workflowMaxCount,
    apply: applyWorkflow,
    clear: clearWorkflow,
  } = useWorkflowMap(riotMapRef);

  // ── Playback map hook ── ← PLAYBACK ──────────────────────────────────────────
  const {
    isActive: playbackActive,
    isLoading: playbackLoading,
    isPlaying: playbackIsPlaying,
    error: playbackError,
    frameCount: playbackFrameCount,
    frameIndex: playbackFrameIndex,
    durationMs: playbackDurationMs,
    speed: playbackSpeed,
    apply: applyPlayback,
    play: playbackPlay,
    pause: playbackPause,
    seek: playbackSeek,
    setSpeed: playbackSetSpeed,
    clear: clearPlayback,
  } = usePlaybackMap(riotMapRef);

  // ── Framer Motion values ─────────────────────────────────────────────────────
  const mvLeft = useMotionValue(0);
  const mvTop = useMotionValue(0);
  const mvWidth = useMotionValue(300);
  const mvHeight = useMotionValue(500);
  const mvRadius = useMotionValue(16);
  const mvZ = useMotionValue(10);
  const mvOpacity = useMotionValue(0);
  const mvEnterY = useMotionValue(16);

  // Page-load entrance animation
  useEffect(() => {
    const t = setTimeout(() => {
      fmAnimate(mvOpacity, 1, { duration: 0.4, ease: "easeOut" });
      fmAnimate(mvEnterY, 0, { duration: 0.4, ease: "easeOut" });
    }, 150);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // ─── Filter query builder ─────────────────────────────────────────────────────
  /**
   * Builds Kinexon Advanced Search filter queries using correct syntax.
   *
   * @param {Object} filters - Filter parameters
   * @param {number} filters.beginTs - Start timestamp (Unix seconds)
   * @param {number} filters.endTs - End timestamp (Unix seconds)
   * @param {string[]} filters.labels - Array of label names to filter by
   * @param {string[]} filters.objectNames - Array of object names to filter by
   * @returns {string} - Kinexon Advanced Search query string
   */
  function buildFilterQuery(filters) {
    const parts = [];

    // Label filtering - use exact match with {obj.labels}
    // {obj.labels} checks against an array, matches any object with that label
    if (filters.labels && filters.labels.length > 0) {
      const labelConditions = filters.labels.map(
        (label) => `{obj.labels} = "${label}"`,
      );
      if (labelConditions.length === 1) {
        parts.push(labelConditions[0]);
      } else {
        // Multiple labels - OR them together
        parts.push(`(${labelConditions.join(" or ")})`);
      }
    }

    // Object name filtering - use partial match with {obj.name} ~ "value"
    // Recommended for search-as-you-type functionality
    if (filters.objectNames && filters.objectNames.length > 0) {
      const nameConditions = filters.objectNames.map(
        (name) => `{obj.name} ~ "${name}"`,
      );
      if (nameConditions.length === 1) {
        parts.push(nameConditions[0]);
      } else {
        // Multiple names - OR them together
        parts.push(`(${nameConditions.join(" or ")})`);
      }
    }

    // Combine all conditions with AND
    const query = parts.join(" and ");

    console.log(`[buildFilterQuery] Generated query: "${query}"`);
    return query;
  }
  // ── filterQuery ref ──────────────────────────────────────────────────────────
  const filterQueryRef = useRef(filterQuery);
  useEffect(() => {
    // Store the actual value, even if null/undefined/empty
    filterQueryRef.current = filterQuery;
    console.log(
      `[RtlsMap FILTER] filterQueryRef updated → "${filterQuery ?? "(null)"}"`,
    );
  }, [filterQuery]);

  // ── applyFilter ──────────────────────────────────────────────────────────────
  const applyFilter = useCallback((query) => {
    if (!riotMapRef.current) {
      console.log(
        `[RtlsMap FILTER] applyFilter: map not ready — will apply on load → "${query ?? "(null/undefined)"}"`,
      );
      return;
    }

    // Normalize null/undefined to empty string (which clears filters)
    const normalizedQuery = query ?? "";

    try {
      console.log(
        `[RtlsMap FILTER] applyFilter: filterObjects("${normalizedQuery}")`,
      );
      riotMapRef.current.filterObjects(normalizedQuery);
      console.log(
        `[RtlsMap FILTER] applyFilter: succeeded → "${normalizedQuery}"`,
      );
    } catch (err) {
      console.warn("[RtlsMap FILTER] applyFilter: filterObjects threw:", err);
    }
  }, []);

  useEffect(() => {
    console.log(
      `[RtlsMap FILTER] filterQuery changed → "${filterQuery ?? "(null)"}" | ` +
        `map ready: ${!!riotMapRef.current}`,
    );

    // Always apply, even if null/undefined/empty (it will clear the filter)
    if (riotMapRef.current) {
      applyFilter(filterQuery);
    }
    // If map isn't ready, it will be applied on load via filterQueryRef
  }, [filterQuery, applyFilter]);

  // Dev console override
  useEffect(() => {
    window.setRtlsFilter = (query) => {
      console.log(
        `[RtlsMap FILTER] window.setRtlsFilter("${query ?? "(null)"}")`,
      );
      applyFilter(query);
    };
    return () => {
      delete window.setRtlsFilter;
    };
  }, [applyFilter]);

  // ── clearAllRtlsVisuals — single source of truth for a full reset ────────────
  // Clears every map-function's drawn shapes AND the object filter/highlight
  // that was applied for it. Each clear*() is a safe no-op if that function
  // isn't currently active, so it's always safe to call all four.
  const clearAllRtlsVisuals = useCallback(() => {
    clearHeatmap();
    clearSpaghetti();
    clearWorkflow();
    clearPlayback();
    applyFilter(filterQuery ?? "");
  }, [
    clearHeatmap,
    clearSpaghetti,
    clearWorkflow,
    clearPlayback,
    applyFilter,
    filterQuery,
  ]);

  // ── Sync with an externally-driven activeRtlsFn ───────────────────────────────
  // Host pages (moduleMetricsPage.js, personnelMetricsPage.js, etc.) drive
  // this map via the activeRtlsFn prop, toggling it straight from one
  // function id to another (or back to null on toggle-off). Without this,
  // that never cleared whatever was previously drawn — whether it came
  // from a prior activeRtlsFn value OR from the in-map buttons — so
  // visualizations stacked. This mirrors the same clear-before-switch rule
  // handleInternalFnBtn already applies to the in-map buttons, so both
  // entry points behave identically and can never leave stale shapes
  // behind for the other to stack on top of.
  useEffect(() => {
    if (prevActiveRtlsFnRef.current !== activeRtlsFn) {
      const prevEffective = prevEffectiveMapFnRef.current;
      if (prevEffective) {
        // Whatever was actually drawn before (internal or external) is
        // being replaced — by a different function, or by nothing.
        clearAllRtlsVisuals();
      }
      if (activeRtlsFn) {
        // An externally-driven function is taking over — make sure no
        // internal (button-driven) popup is left open underneath it.
        cancelFilterClose();
        setInternalMapFn(null);
      }
      prevActiveRtlsFnRef.current = activeRtlsFn;
    }
    prevEffectiveMapFnRef.current = effectiveMapFn;
  }, [activeRtlsFn, effectiveMapFn, cancelFilterClose, clearAllRtlsVisuals]);

  const handleFilterClose = useCallback(() => {
    if (filterCloseTimerRef.current) return;

    // Clear every feature's shapes + the object filter/highlight.
    clearAllRtlsVisuals();

    setIsFilterClosing(true);
    filterCloseTimerRef.current = setTimeout(() => {
      filterCloseTimerRef.current = null;
      setInternalMapFn(null);
      setIsFilterClosing(false);
      onRtlsFnClose?.();
    }, FILTER_CLOSE_MS);
  }, [onRtlsFnClose, clearAllRtlsVisuals]);

  // ── FIX: handleInternalFnBtn — always clears the active feature on toggle-off ─
  const handleInternalFnBtn = useCallback(
    (fn) => {
      setInternalMapFn((prev) => {
        if (prev === fn) {
          // Toggle off — unconditionally clear all (no stale-closure issue)
          clearAllRtlsVisuals();
          beginFilterClose(() => setInternalMapFn(null));
          return prev;
        }
        // Switching to a different function — clear whatever was previously
        // active (shapes + object filter) BEFORE swapping, so visualizations
        // don't stack on top of each other.
        if (prev) {
          clearAllRtlsVisuals();
        }
        cancelFilterClose();
        return fn;
      });
    },
    [clearAllRtlsVisuals, beginFilterClose, cancelFilterClose],
  );

  // ── handleRtlsFilterApply ────────────────────────────────────────────────────
  const handleRtlsFilterApply = useCallback(
    async (filters) => {
      const fn = effectiveMapFnRef.current;

      console.log(`[RtlsMap] handleRtlsFilterApply: fn="${fn}"`, filters);

      if (!fn) {
        console.warn(
          "[RtlsMap] handleRtlsFilterApply: effectiveMapFn is null — " +
            "no function button is active.",
        );
        return;
      }

      // Build the filter query using correct Kinexon syntax
      const filterQuery = buildFilterQuery(filters);

      switch (fn) {
        case "heatmap": {
          await applyHeatmap(filters, {
            locationId: RTLS_LOCATION_ID,
            showLabels: false,
          });

          // Apply object filter to map if labels or names were provided
          if (filterQuery && riotMapRef.current) {
            try {
              console.log(`[RtlsMap] Applying filter query: "${filterQuery}"`);
              riotMapRef.current.filterObjects(filterQuery);
            } catch (err) {
              console.warn("[RtlsMap] filterObjects failed:", err);
            }
          }
          break;
        }

        case "spaghetti": {
          await applySpaghetti(filters, { samplingPeriod: 60 });

          // Apply object filter to map if labels or names were provided
          if (filterQuery && riotMapRef.current) {
            try {
              console.log(`[RtlsMap] Applying filter query: "${filterQuery}"`);
              riotMapRef.current.filterObjects(filterQuery);
            } catch (err) {
              console.warn("[RtlsMap] filterObjects failed:", err);
            }
          }
          break;
        }

        case "workflow": {
          // Filters from rtlsFilter.js workflow branch:
          //   { objectName, jobName, jobNumber, beginTs, endTs }
          //
          // applyWorkflow() handles both module (12WO prefix) and personnel
          // paths internally, fetches SQL windows + position history via APIM,
          // bins positions client-side, and draws Cylinder heatmap shapes.
          //
          // We also call filterObjects() so the live badge for this object
          // stays highlighted on the map while the workflow heatmap is active.
          await applyWorkflow(filters, { samplingPeriod: 30 });

          const workflowQuery = filters.objectName
            ? `{obj.name} = "${filters.objectName}"`
            : "";
          if (workflowQuery && riotMapRef.current) {
            try {
              console.log(
                `[RtlsMap] Workflow — applying object filter: "${workflowQuery}"`,
              );
              riotMapRef.current.filterObjects(workflowQuery);
            } catch (err) {
              console.warn("[RtlsMap] filterObjects failed:", err);
            }
          }
          break;
        }
        case "playback": {
          // filters from rtlsFilter.js playback branch:
          //   { datetimeTs: number, endDatetimeTs: number }
          //
          // The object to track is the one currently locked in the filter panel.
          // rtlsSelectedObject carries the name when launched from an external
          // context (e.g. a personnel page); in full/internal mode the user has
          // not selected an object via the playback filter panel (playback mode
          // only shows datetime pickers, not an object picker), so we fall back
          // to the first objectName in the broader filters if present.
          const playbackObjectName =
            rtlsSelectedObject ??
            (Array.isArray(filters.objectNames)
              ? filters.objectNames[0]
              : null) ??
            null;

          await applyPlayback(filters, playbackObjectName, {
            samplingPeriod: 1,
          });

          // Highlight the tracked object on the live map while playback is active
          const playbackFilterQuery = playbackObjectName
            ? `{obj.name} = "${playbackObjectName}"`
            : "";
          if (playbackFilterQuery && riotMapRef.current) {
            try {
              console.log(
                `[RtlsMap] Playback — applying object filter: "${playbackFilterQuery}"`,
              );
              riotMapRef.current.filterObjects(playbackFilterQuery);
            } catch (err) {
              console.warn("[RtlsMap] filterObjects failed:", err);
            }
          }
          break;
        }
        default:
          console.warn("[RtlsMap] Unknown RTLS function:", fn);
      }

      onRtlsFilterApply?.({ fn, ...filters });
    },
    [
      applyHeatmap,
      applySpaghetti,
      applyWorkflow,
      applyPlayback,
      onRtlsFilterApply,
    ],
  );

  // ── Resize helpers ───────────────────────────────────────────────────────────
  const notifyMapResize = useCallback(() => {
    if (!riotMapRef.current) return;
    try {
      if (dynamicResizeRef.current) dynamicResizeRef.current();
      else riotMapRef.current.call("windowResize");
    } catch (_) {}
  }, []);

  const scheduleMapResize = useCallback(() => {
    if (resizeRafRef.current !== null) return;
    resizeRafRef.current = requestAnimationFrame(() => {
      resizeRafRef.current = null;
      notifyMapResize();
    });
  }, [notifyMapResize]);

  const detectResizeMethods = useCallback((mapInstance) => {
    setTimeout(() => {
      if (!mountedRef.current || !mapInstance) return;
      if (typeof mapInstance.resize === "function")
        dynamicResizeRef.current = () => mapInstance.resize();
      else if (typeof mapInstance.windowResize === "function")
        dynamicResizeRef.current = () => mapInstance.windowResize();
      else dynamicResizeRef.current = () => mapInstance.call("windowResize");
    }, 200);
  }, []);

  // ── Sync portal card to spacer ───────────────────────────────────────────────
  const syncToSpacer = useCallback(() => {
    if (!spacerRef.current || isExpandedRef.current) return;
    const r = spacerRef.current.getBoundingClientRect();
    mvLeft.set(r.left);
    mvTop.set(r.top);
    mvWidth.set(r.width);
    mvHeight.set(r.height);
  }, [mvLeft, mvTop, mvWidth, mvHeight]);

  useLayoutEffect(() => {
    syncToSpacer();
  }, []); // eslint-disable-line

  useEffect(() => {
    const ro = new ResizeObserver(syncToSpacer);
    if (spacerRef.current) ro.observe(spacerRef.current);
    window.addEventListener("resize", syncToSpacer);
    window.addEventListener("scroll", syncToSpacer, {
      capture: true,
      passive: true,
    });
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", syncToSpacer);
      window.removeEventListener("scroll", syncToSpacer, { capture: true });
    };
  }, [syncToSpacer]);

  useMotionValueEvent(mvHeight, "change", scheduleMapResize);
  useMotionValueEvent(mvWidth, "change", scheduleMapResize);

  useEffect(
    () => () => {
      if (resizeRafRef.current !== null) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    document.body.style.overflow = isExpanded ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isExpanded]);

  useEffect(() => {
    if (!isExpanded) return;
    const onKey = (e) => {
      if (e.key === "Escape") toggleExpand();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Teardown ─────────────────────────────────────────────────────────────────
  const teardown = useCallback(() => {
    if (posSubRef.current) {
      try {
        posSubRef.current.unsubscribe();
      } catch (_) {}
      posSubRef.current = null;
    }
    if (riotMapRef.current && mapContainerRef.current) {
      try {
        mapContainerRef.current.innerHTML = "";
      } catch (_) {}
      riotMapRef.current = null;
    }
    const unsubs = unsubscribersRef.current;
    if (apiRef.current) {
      try {
        if (typeof unsubs.connectionStatus === "function")
          unsubs.connectionStatus();
        if (typeof unsubs.error === "function") unsubs.error();
        if (typeof unsubs.identityReset === "function") unsubs.identityReset();
      } catch (_) {}
    }
    unsubscribersRef.current = {
      connectionStatus: null,
      error: null,
      identityReset: null,
    };
    apiRef.current = null;
    riotRef.current = null;
    loggedInRef.current = false;
    dynamicResizeRef.current = null;
    setLivePositions([]);
  }, []);

  // ── renderMap ─────────────────────────────────────────────────────────────────
  const renderMap = useCallback(
    (Riot) => {
      if (!mountedRef.current) return;
      const container = mapContainerRef.current;
      if (!container) {
        console.warn("[RtlsMap] Map container not available");
        return;
      }

      Riot.Api.locationApi
        .getLocationList()
        .then((locations) => {
          if (!mountedRef.current || !locations || locations.length === 0) {
            console.warn("[RtlsMap] No locations available");
            return;
          }

          const location =
            locations.find((l) => l.id === 8) ??
            (() => {
              console.warn(
                "[RtlsMap] Location id=8 not found — falling back to locations[0]",
              );
              return locations[0];
            })();

          const config = location.configs && location.configs[0];
          if (!config) {
            console.warn("[RtlsMap] No config found for location");
            return;
          }

          if (riotMapRef.current) {
            container.innerHTML = "";
            riotMapRef.current = null;
          }

          const map = new Riot.Map(container);
          riotMapRef.current = map;

          map.on("load", (success) => {
            if (!success) {
              console.warn("[RtlsMap] Map failed to load");
              return;
            }
            try {
              map.switchMode(Riot.Map.MODE_2D);
              map.toggleObjects(true, false);
              map.toggleGeofences(false);
            } catch (err) {
              console.warn("[RtlsMap] Error configuring map:", err);
            }
            detectResizeMethods(map);
          });

          map.on("load", (success) => {
            if (!success) return;
            try {
              map.switchMode(Riot.Map.MODE_2D);
              map.toggleObjects(true, false);
              map.toggleGeofences(false);
            } catch (_) {}
            detectResizeMethods(map);

            const pending = filterQueryRef.current;
            // Apply whatever filter was set, including empty string (clears filter)
            // Only skip if explicitly null or undefined
            if (pending !== null && pending !== undefined) {
              console.log(
                `[RtlsMap FILTER] load: applying filter → "${pending}"`,
              );
              try {
                map.filterObjects(pending);
              } catch (err) {
                console.warn(
                  "[RtlsMap FILTER] load: filterObjects threw:",
                  err,
                );
              }
            } else {
              // No filter prop was passed, explicitly clear to show all objects
              console.log(
                "[RtlsMap FILTER] load: clearing filter to show all objects",
              );
              try {
                map.filterObjects("");
              } catch (err) {
                console.warn("[RtlsMap FILTER] load: clear filter threw:", err);
              }
            }
          });

          map.on("no-location", () => {
            map.setLocation(location.id, config.id);
          });
          map.render();
          map.setLocation(location.id, config.id);
        })
        .catch((err) => {
          console.error("[RtlsMap] Failed to get locations:", err);
        });
    },
    [detectResizeMethods],
  );

  // ── subscribePositions ───────────────────────────────────────────────────────
  const subscribePositions = useCallback((Riot) => {
    if (!mountedRef.current) return;
    if (posSubRef.current) {
      try {
        posSubRef.current.unsubscribe();
      } catch (_) {}
    }
    try {
      posSubRef.current = Riot.Api.positionApi.rsig.positionUpdated
        .register()
        .subscribe((pos) => {
          if (!mountedRef.current) return;
          setLivePositions((prev) => {
            const idx = prev.findIndex((p) => p.objectId === pos.objectId);
            if (idx >= 0) {
              const next = [...prev];
              next[idx] = pos;
              return next;
            }
            return [...prev, pos];
          });
        });
    } catch (err) {
      console.error("[RtlsMap] Failed to subscribe to positions:", err);
    }
  }, []);

  // ── authenticate ─────────────────────────────────────────────────────────────
  const authenticate = useCallback(
    (Riot) => {
      if (!mountedRef.current || loggedInRef.current) return;
      Riot.Api.userApi
        .isLoggedIn()
        .then((isLoggedIn) => {
          if (!mountedRef.current) return;
          if (isLoggedIn) {
            Riot.Api.userApi.selectRole(10).then((roleSuccess) => {
              if (!mountedRef.current) return;
              if (!roleSuccess) {
                console.error(
                  "[RtlsMap] Role selection failed (already logged in)",
                );
                setConnStatus(CONN.ERROR);
                return;
              }
              loggedInRef.current = true;
              setConnStatus(CONN.CONNECTED);
              renderMap(Riot);
              subscribePositions(Riot);
            });
          } else {
            Riot.Api.userApi
              .login(RTLS_USER, RTLS_PASS)
              .then(([loginSuccess]) => {
                if (!mountedRef.current) return;
                if (!loginSuccess) {
                  console.error("[RtlsMap] Login failed");
                  setConnStatus(CONN.ERROR);
                  return;
                }
                Riot.Api.userApi.selectRole(10).then((roleSuccess) => {
                  if (!mountedRef.current) return;
                  if (!roleSuccess) {
                    console.error("[RtlsMap] Role selection failed");
                    setConnStatus(CONN.ERROR);
                    return;
                  }
                  loggedInRef.current = true;
                  setConnStatus(CONN.CONNECTED);
                  renderMap(Riot);
                  subscribePositions(Riot);
                });
              })
              .catch((err) => {
                if (!mountedRef.current) return;
                console.error("[RtlsMap] Login error:", err);
                setConnStatus(CONN.ERROR);
              });
          }
        })
        .catch((err) => {
          if (!mountedRef.current) return;
          console.error("[RtlsMap] Auth check error:", err);
          setConnStatus(CONN.ERROR);
        });
    },
    [renderMap, subscribePositions],
  );

  // ── initializeKinexon ─────────────────────────────────────────────────────────
  const initializeKinexon = useCallback(() => {
    if (hasInitializedRef.current || !mountedRef.current) return;
    hasInitializedRef.current = true;
    setConnStatus(CONN.CONNECTING);

    loadKinexonScript((success) => {
      if (!mountedRef.current) return;
      if (!success) {
        console.error("[RtlsMap] Script load failed");
        setConnStatus(CONN.ERROR);
        return;
      }
      const Riot = window.Riot;
      if (!Riot) {
        console.error("[RtlsMap] window.Riot is undefined");
        setConnStatus(CONN.ERROR);
        return;
      }
      riotRef.current = Riot;

      try {
        if (!globalRiotInitialized) {
          Riot.init(RTLS_URL);
          Riot.Api.locationApi || Riot.init(RTLS_URL);
          const map = riotMapRef.current;
          if (map) {
            map.setGlobalPosition(33.21572849, -111.8392031);
          } else {
            Riot._pendingGlobalPosition = [33.21572849, -111.8392031];
          }
          globalRiotInitialized = true;
        }
        if (!Riot.Api) {
          console.error("[RtlsMap] Riot.Api is undefined after Riot.init()");
          setConnStatus(CONN.ERROR);
          return;
        }
        const Api = Riot.Api;
        apiRef.current = Api;

        if (Api.rmi && Api.rmi.isRunning && Api.rmi.isRunning()) {
          setConnStatus(CONN.CONNECTED);
          authenticate(Riot);
          return;
        }

        const connStatusUnsub = Api.on("connectionStatus", (status) => {
          if (!mountedRef.current) return;
          if (status === 1) {
            setConnStatus(CONN.CONNECTED);
            authenticate(Riot);
          } else if (status === 2) {
            loggedInRef.current = false;
            setConnStatus(CONN.RECONNECTING);
          } else {
            setConnStatus(CONN.IDLE);
          }
        });
        if (typeof connStatusUnsub === "function")
          unsubscribersRef.current.connectionStatus = connStatusUnsub;

        const errorUnsub = Api.on("error", (err) => {
          if (!mountedRef.current) return;
          console.warn("[RtlsMap] WebSocket error:", err);
          setConnStatus(CONN.RECONNECTING);
        });
        if (typeof errorUnsub === "function")
          unsubscribersRef.current.error = errorUnsub;

        const identityResetUnsub = Api.on("identityReset", () => {
          if (!mountedRef.current) return;
          loggedInRef.current = false;
          setConnStatus(CONN.RECONNECTING);
          if (riotRef.current) authenticate(riotRef.current);
        });
        if (typeof identityResetUnsub === "function")
          unsubscribersRef.current.identityReset = identityResetUnsub;

        Api.init();
      } catch (err) {
        console.error("[RtlsMap] Initialization error:", err);
        setConnStatus(CONN.ERROR);
      }
    });
  }, [authenticate]);

  // ── Mount / unmount ──────────────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    initializeKinexon();
    return () => {
      mountedRef.current = false;
      teardown();
      setTimeout(() => {
        if (!mountedRef.current) hasInitializedRef.current = false;
      }, 100);
    };
  }, [initializeKinexon, teardown]);

  // ── handleMapHome ─────────────────────────────────────────────────────────────
  const handleMapHome = useCallback(async () => {
    if (!riotMapRef.current) return;
    try {
      riotMapRef.current.setPosition(-76.765, 19.271, 1.0);
    } catch (err) {
      console.warn("[RtlsMap] Error recentering map:", err);
    }
  }, []);

  // ── toggleExpand ──────────────────────────────────────────────────────────────
  const toggleExpand = useCallback(() => {
    const nextExpanded = !isExpandedRef.current;
    isExpandedRef.current = nextExpanded;
    setIsExpanded(nextExpanded);

    if (nextExpanded) {
      if (spacerRef.current) {
        const r = spacerRef.current.getBoundingClientRect();
        mvLeft.set(r.left);
        mvTop.set(r.top);
        mvWidth.set(r.width);
        mvHeight.set(r.height);
      }
      mvZ.set(500);
      fmAnimate(mvLeft, 0, EXPAND_SPRING);
      fmAnimate(mvTop, 0, EXPAND_SPRING);
      fmAnimate(mvWidth, window.innerWidth, EXPAND_SPRING);
      fmAnimate(mvRadius, 0, EXPAND_SPRING);
      fmAnimate(mvHeight, window.innerHeight, EXPAND_SPRING).then(() => {
        if (mountedRef.current) notifyMapResize();
      });
    } else {
      const r = spacerRef.current?.getBoundingClientRect();
      if (r) {
        fmAnimate(mvLeft, r.left, EXPAND_SPRING);
        fmAnimate(mvTop, r.top, EXPAND_SPRING);
        fmAnimate(mvWidth, r.width, EXPAND_SPRING);
        fmAnimate(mvRadius, 16, EXPAND_SPRING);
        fmAnimate(mvHeight, r.height, EXPAND_SPRING).then(() => {
          mvZ.set(10);
          if (mountedRef.current) notifyMapResize();
        });
      }
    }
  }, [mvLeft, mvTop, mvWidth, mvHeight, mvRadius, mvZ, notifyMapResize]);

  const showExtraButtons = isHomePage || isExpanded;

  // ── Card JSX ──────────────────────────────────────────────────────────────────
  const cardContent = (
    <>
      {/* ── Section header ──────────────────────────────────────────────── */}
      <div className="rtls-section-header">
        <div className="rtls-header-left">
          <h2>Live Facility Map</h2>
          <div className="rtls-conn-indicator">
            <div
              className={`rtls-conn-dot${
                connStatus === CONN.RECONNECTING
                  ? " rtls-conn-dot--reconnecting"
                  : ""
              }`}
              style={{ background: CONN_COLORS[connStatus] }}
            />
            <span className="rtls-conn-label">{CONN_LABELS[connStatus]}</span>
          </div>
        </div>

        <div className="rtls-map-controls">
          {/* Recenter */}
          <button
            className="rtls-map-control-btn"
            onClick={handleMapHome}
            title="Recenter map"
            disabled={connStatus !== CONN.CONNECTED}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </button>

          {/* Expand / collapse */}
          <button
            className={`rtls-map-control-btn${isExpanded ? " active" : ""}`}
            onClick={toggleExpand}
            title={isExpanded ? "Exit fullscreen" : "Expand fullscreen"}
          >
            {isExpanded ? (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
              </svg>
            )}
          </button>

          {showExtraButtons && (
            <>
              {/* Heatmap */}
              <button
                className={`rtls-map-control-btn${
                  internalMapFn === "heatmap" || heatmapActive ? " active" : ""
                }`}
                onClick={() => {
                  if (heatmapActive) {
                    clearAllRtlsVisuals();
                    beginFilterClose(() => setInternalMapFn(null));
                  } else {
                    handleInternalFnBtn("heatmap");
                  }
                }}
                title={
                  heatmapLoading
                    ? "Loading heatmap…"
                    : heatmapActive
                      ? "Clear heatmap"
                      : "Heatmap"
                }
                disabled={connStatus !== CONN.CONNECTED || heatmapLoading}
              >
                {heatmapLoading ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ animation: "rtlsSpin 0.75s linear infinite" }}
                  >
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="3" />
                    <circle cx="12" cy="12" r="7" opacity="0.5" />
                    <circle cx="12" cy="12" r="10" opacity="0.25" />
                  </svg>
                )}
              </button>

              {/* Spaghetti Map ← SPAGHETTI */}
              <button
                className={`rtls-map-control-btn${
                  internalMapFn === "spaghetti" || spaghettiActive
                    ? " active"
                    : ""
                }`}
                onClick={() => {
                  if (spaghettiActive) {
                    clearAllRtlsVisuals();
                    beginFilterClose(() => setInternalMapFn(null));
                  } else {
                    handleInternalFnBtn("spaghetti");
                  }
                }}
                title={
                  spaghettiLoading
                    ? "Loading spaghetti map…"
                    : spaghettiActive
                      ? "Clear spaghetti map"
                      : "Spaghetti Map"
                }
                disabled={connStatus !== CONN.CONNECTED || spaghettiLoading}
              >
                {spaghettiLoading ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ animation: "rtlsSpin 0.75s linear infinite" }}
                  >
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M3 12c0-4 2-8 6-8s6 4 6 8-2 8-6 8" />
                    <path d="M15 4c4 0 6 4 6 8s-2 8-6 8" />
                  </svg>
                )}
              </button>

              {/* Playback ← PLAYBACK */}
              <button
                className={`rtls-map-control-btn${
                  internalMapFn === "playback" || playbackActive
                    ? " active"
                    : ""
                }`}
                onClick={() => {
                  if (playbackActive) {
                    clearAllRtlsVisuals();
                    beginFilterClose(() => setInternalMapFn(null));
                  } else {
                    handleInternalFnBtn("playback");
                  }
                }}
                title={
                  playbackLoading
                    ? "Loading playback data…"
                    : playbackActive
                      ? "Stop playback"
                      : "Playback"
                }
                disabled={connStatus !== CONN.CONNECTED || playbackLoading}
              >
                {playbackLoading ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ animation: "rtlsSpin 0.75s linear infinite" }}
                  >
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                )}
              </button>

              {/* Workflow Map ← WORKFLOW */}
              <button
                className={`rtls-map-control-btn${
                  internalMapFn === "workflow" || workflowActive
                    ? " active"
                    : ""
                }`}
                onClick={() => {
                  if (workflowActive) {
                    clearAllRtlsVisuals();
                    beginFilterClose(() => setInternalMapFn(null));
                  } else {
                    handleInternalFnBtn("workflow");
                  }
                }}
                title={
                  workflowLoading
                    ? "Loading workflow map…"
                    : workflowActive
                      ? "Clear workflow map"
                      : "Workflow Map"
                }
                disabled={connStatus !== CONN.CONNECTED || workflowLoading}
              >
                {workflowLoading ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ animation: "rtlsSpin 0.75s linear infinite" }}
                  >
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="3" y="3" width="7" height="7" rx="1" />
                    <rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" />
                    <path d="M17.5 14v3M17.5 17h-3.5" strokeLinecap="round" />
                  </svg>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Heatmap status bar ───────────────────────────────────────────── */}
      {(heatmapActive || heatmapLoading || heatmapError) && (
        <div
          className={`rtls-heatmap-status${
            heatmapError
              ? " rtls-heatmap-status--error"
              : heatmapLoading
                ? " rtls-heatmap-status--loading"
                : " rtls-heatmap-status--active"
          }`}
        >
          {heatmapLoading && (
            <>
              <div className="rtls-heatmap-status-spinner" />
              <span>Fetching heatmap data…</span>
            </>
          )}
          {heatmapError && !heatmapLoading && (
            <>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <circle cx="12" cy="16" r="0.5" fill="currentColor" />
              </svg>
              <span>{heatmapError}</span>
              <button
                className="rtls-heatmap-status-dismiss"
                onClick={handleFilterClose}
                aria-label="Dismiss error"
              >
                ✕
              </button>
            </>
          )}
          {heatmapActive && !heatmapLoading && !heatmapError && (
            <>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="3" />
                <circle cx="12" cy="12" r="7" opacity="0.5" />
                <circle cx="12" cy="12" r="10" opacity="0.25" />
              </svg>
              <span>
                Heatmap active &mdash; {heatmapCellCount.toLocaleString()} cells
                rendered
              </span>
              <button
                className="rtls-heatmap-status-dismiss"
                onClick={handleFilterClose}
                aria-label="Clear heatmap"
              >
                Clear
              </button>
              <div className="rtls-heatmap-status-legend">
                {heatmapMinCount === heatmapMaxCount ? (
                  <span className="rtls-legend-item">
                    All cells: {heatmapMinCount.toLocaleString()} position
                    {heatmapMinCount !== 1 ? "s" : ""}
                  </span>
                ) : (
                  <>
                    <span className="rtls-heat-legend-count">
                      {heatmapMinCount.toLocaleString()}
                    </span>
                    <span
                      className="rtls-heat-legend-bar"
                      style={{ background: HEAT_GRADIENT_CSS }}
                    />
                    <span className="rtls-heat-legend-count">
                      {heatmapMaxCount.toLocaleString()}
                    </span>
                    <span className="rtls-heat-legend-caption">
                      positions per cell
                    </span>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Spaghetti map status bar ← SPAGHETTI ────────────────────────── */}
      {(spaghettiActive || spaghettiLoading || spaghettiError) && (
        <div
          className={`rtls-heatmap-status${
            spaghettiError
              ? " rtls-heatmap-status--error"
              : spaghettiLoading
                ? " rtls-heatmap-status--loading"
                : " rtls-heatmap-status--active"
          }`}
        >
          {spaghettiLoading && (
            <>
              <div className="rtls-heatmap-status-spinner" />
              <span>Fetching position history…</span>
            </>
          )}
          {spaghettiError && !spaghettiLoading && (
            <>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <circle cx="12" cy="16" r="0.5" fill="currentColor" />
              </svg>
              <span>{spaghettiError}</span>
              <button
                className="rtls-heatmap-status-dismiss"
                onClick={handleFilterClose}
                aria-label="Dismiss error"
              >
                ✕
              </button>
            </>
          )}
          {spaghettiActive && !spaghettiLoading && !spaghettiError && (
            <>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 12c0-4 2-8 6-8s6 4 6 8-2 8-6 8" />
                <path d="M15 4c4 0 6 4 6 8s-2 8-6 8" />
              </svg>
              <span>
                Spaghetti map active &mdash; {spaghettiTrackCount} track
                {spaghettiTrackCount !== 1 ? "s" : ""},{" "}
                {spaghettiPointCount.toLocaleString()} points
              </span>
              <button
                className="rtls-heatmap-status-dismiss"
                onClick={handleFilterClose}
                aria-label="Clear spaghetti map"
              >
                Clear
              </button>
              <div className="rtls-heatmap-status-legend">
                <span className="rtls-legend-item">
                  <span
                    className="rtls-legend-swatch"
                    style={{ background: START_MARKER_COLOR_CSS }}
                  />
                  Start
                </span>
                <span className="rtls-legend-item">
                  <span
                    className="rtls-legend-swatch"
                    style={{ background: END_MARKER_COLOR_CSS }}
                  />
                  End
                </span>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Workflow map status bar ← WORKFLOW ──────────────────────────── */}
      {(workflowActive || workflowLoading || workflowError) && (
        <div
          className={`rtls-heatmap-status${
            workflowError
              ? " rtls-heatmap-status--error"
              : workflowLoading
                ? " rtls-heatmap-status--loading"
                : " rtls-heatmap-status--active"
          }`}
        >
          {workflowLoading && (
            <>
              <div className="rtls-heatmap-status-spinner" />
              <span>Fetching workflow map data…</span>
            </>
          )}
          {workflowError && !workflowLoading && (
            <>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <circle cx="12" cy="16" r="0.5" fill="currentColor" />
              </svg>
              <span>{workflowError}</span>
              <button
                className="rtls-heatmap-status-dismiss"
                onClick={handleFilterClose}
                aria-label="Dismiss error"
              >
                ✕
              </button>
            </>
          )}
          {workflowActive && !workflowLoading && !workflowError && (
            <>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <path d="M17.5 14v3M17.5 17h-3.5" strokeLinecap="round" />
              </svg>
              <span>
                Workflow map active &mdash; {workflowBinCount.toLocaleString()}{" "}
                cells, {workflowWindowCount} job window
                {workflowWindowCount !== 1 ? "s" : ""}
                {workflowPath ? ` (${workflowPath})` : ""}
              </span>
              <button
                className="rtls-heatmap-status-dismiss"
                onClick={handleFilterClose}
                aria-label="Clear workflow map"
              >
                Clear
              </button>
              <div className="rtls-heatmap-status-legend">
                {workflowMinCount === workflowMaxCount ? (
                  <span className="rtls-legend-item">
                    All cells: {workflowMinCount.toLocaleString()} position
                    {workflowMinCount !== 1 ? "s" : ""}
                  </span>
                ) : (
                  <>
                    <span className="rtls-heat-legend-count">
                      {workflowMinCount.toLocaleString()}
                    </span>
                    <span
                      className="rtls-heat-legend-bar"
                      style={{ background: WORKFLOW_HEAT_GRADIENT_CSS }}
                    />
                    <span className="rtls-heat-legend-count">
                      {workflowMaxCount.toLocaleString()}
                    </span>
                    <span className="rtls-heat-legend-caption">
                      positions per cell
                    </span>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
      {/* ── Playback status bar ← PLAYBACK ──────────────────────────────── */}
      {(playbackActive || playbackLoading || playbackError) && (
        <div
          className={`rtls-heatmap-status${
            playbackError
              ? " rtls-heatmap-status--error"
              : playbackLoading
                ? " rtls-heatmap-status--loading"
                : " rtls-heatmap-status--active"
          }`}
        >
          {playbackLoading && (
            <>
              <div className="rtls-heatmap-status-spinner" />
              <span>Fetching playback data…</span>
            </>
          )}

          {playbackError && !playbackLoading && (
            <>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <circle cx="12" cy="16" r="0.5" fill="currentColor" />
              </svg>
              <span>{playbackError}</span>
              <button
                className="rtls-heatmap-status-dismiss"
                onClick={handleFilterClose}
                aria-label="Dismiss error"
              >
                ✕
              </button>
            </>
          )}

          {playbackActive && !playbackLoading && !playbackError && (
            <>
              {/* ── Play / Pause toggle ──────────────────────────────────── */}
              <button
                className="rtls-heatmap-status-dismiss"
                onClick={playbackIsPlaying ? playbackPause : playbackPlay}
                aria-label={
                  playbackIsPlaying ? "Pause playback" : "Resume playback"
                }
                title={playbackIsPlaying ? "Pause" : "Play"}
              >
                {playbackIsPlaying ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ width: 14, height: 14, display: "block" }}
                  >
                    <rect x="6" y="4" width="4" height="16" rx="1" />
                    <rect x="14" y="4" width="4" height="16" rx="1" />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ width: 14, height: 14, display: "block" }}
                  >
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                )}
              </button>

              {/* ── Scrubber ────────────────────────────────────────────── */}
              <input
                type="range"
                min={0}
                max={Math.max(0, playbackFrameCount - 1)}
                value={playbackFrameIndex}
                onChange={(e) => playbackSeek(Number(e.target.value))}
                style={{
                  flex: 1,
                  minWidth: 60,
                  maxWidth: 140,
                  cursor: "pointer",
                }}
                aria-label="Playback position"
              />

              {/* ── Frame counter ────────────────────────────────────────── */}
              <span
                style={{ fontVariantNumeric: "tabular-nums", minWidth: 72 }}
              >
                {playbackFrameIndex + 1} / {playbackFrameCount}
              </span>

              {/* ── Speed selector ──────────────────────────────────────── */}
              <select
                value={playbackSpeed}
                onChange={(e) => playbackSetSpeed(Number(e.target.value))}
                style={{
                  fontSize: "0.75rem",
                  padding: "1px 4px",
                  borderRadius: 4,
                }}
                aria-label="Playback speed"
              >
                {PLAYBACK_SPEEDS.map((s) => (
                  <option key={s} value={s}>
                    {s >= 3600
                      ? `${s / 3600}hr/s`
                      : s >= 60
                        ? `${s / 60}min/s`
                        : `${s}s/s`}
                  </option>
                ))}
              </select>

              {/* ── Stop / clear ────────────────────────────────────────── */}
              <button
                className="rtls-heatmap-status-dismiss"
                onClick={handleFilterClose}
                aria-label="Stop playback"
              >
                Clear
              </button>
            </>
          )}
        </div>
      )}
      {/* ── Workflow map status bar ← WORKFLOW ──────────────────────────── */}
      {/* ── Map area ─────────────────────────────────────────────────────── */}
      {connStatus === CONN.ERROR ? (
        <div className="rtls-map-placeholder rtls-map-placeholder--error">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <circle cx="12" cy="16" r="0.5" fill="currentColor" />
          </svg>
          <p>Unable to connect to RTLS</p>
          <span className="rtls-placeholder-note">
            Check that the Kinexon server is reachable at{" "}
            <code>{RTLS_URL}</code> and that credentials are correct. Verify the
            jsapi.js script is accessible and CORS is enabled.
          </span>
        </div>
      ) : (
        <div
          ref={mapContainerRef}
          className={`rtls-map-iframe-wrap${
            connStatus !== CONN.CONNECTED
              ? " rtls-map-iframe-wrap--loading"
              : ""
          }`}
        >
          {connStatus !== CONN.CONNECTED && (
            <div className="rtls-map-loading-overlay">
              <div className="rtls-spinner" />
              <span>{CONN_LABELS[connStatus]}…</span>
            </div>
          )}

          {effectiveMapFn && (
            <RtlsFilter
              activeFunction={effectiveMapFn}
              mode={filterMode}
              selectedObject={rtlsSelectedObject}
              isExpanded={isExpanded}
              isClosingExternal={isFilterClosing}
              onApply={handleRtlsFilterApply}
              onClose={handleFilterClose}
            />
          )}
        </div>
      )}
    </>
  );

  // ── Portal render ────────────────────────────────────────────────────────────
  return (
    <>
      <div ref={spacerRef} className="rtls-map-spacer" aria-hidden="true" />
      {createPortal(
        <motion.div
          className={`rtls-map-container${isExpanded ? " rtls-map-container--expanded" : ""}`}
          style={{
            position: "fixed",
            left: mvLeft,
            top: mvTop,
            width: mvWidth,
            height: mvHeight,
            borderRadius: mvRadius,
            zIndex: mvZ,
            opacity: mvOpacity,
            y: mvEnterY,
            boxSizing: "border-box",
            transition: "none",
          }}
        >
          {cardContent}
        </motion.div>,
        document.body,
      )}
    </>
  );
};

export default RtlsMap;
